import {
  Fragment,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { compatWrapper } from '@backstage/core-compat-api';
import { AppRootWrapperBlueprint } from '@backstage/plugin-app-react';

import { ConfidenceProvider } from '@spotify-confidence/react';
import { Banner, ProgressCircle, Text } from '@spotify-internal/encore-web';
import { useRefreshBillingAfterCheckout } from '@spotify/confidence-billing-react';
import {
  ConfidenceCoreProvider,
  LoginPage,
  ResourceLinkExtensions,
  getCurrentLocationPath,
  hasOAuthCallbackParams,
  useAuth,
  useRegion,
  useSentryScope,
} from '@spotify/confidence-core-react';
import {
  FlagEvaluationProvider,
  useFeatureFlag,
} from '@spotify/confidence-flag-evaluation-react';
import {
  getTypeOrNull,
  useAccountSetupQuery,
} from '@spotify/confidence-graphql';
import { TourResumer } from '@spotify/confidence-onboarding-react';
import { defaultResourceLinkExtensionElements } from '@spotify/plugin-confidence-app-module-extensions';
import {
  AppAccountRedirect,
  AppRedirectPage,
  useAppRedirect,
} from '@spotify/plugin-confidence-app-react';
import { RunningChatsProvider } from '@spotify/plugin-confidence-chat-react';
import { FlagResourceLinkExtension } from '@spotify/plugin-confidence-flags';
import { PulseCaptureNotificationsProvider } from '@spotify/plugin-confidence-pulse-react';
import {
  PageTrackingGuard,
  PageTrackingProvider,
  useAutoTracking,
} from '@spotify/plugin-confidence-tracking-react';

import { confidenceApi } from '../apis/Apis';
import {
  clearInvitationLoginReturn,
  consumeInvitationLoginReturnRoute,
} from '../utils/invitationLoginReturn';
import {
  getOrganizationInvitationRoute,
  isPublicRoute,
} from '../utils/publicRoutes';
import { FlagContext } from './FlagContext';

import styles from './RootWrapper.module.css';

const resourceLinkExtensions = (
  <ResourceLinkExtensions>
    <FlagResourceLinkExtension />
    {defaultResourceLinkExtensionElements}
  </ResourceLinkExtensions>
);

const LoadingPage = () => (
  <div className={styles.loading}>
    <ProgressCircle aria-label="Loading application" size="medium" />
  </div>
);

const BlockedAccountBanner = () => {
  const { data } = useAccountSetupQuery({ fetchPolicy: 'cache-and-network' });
  const currentAccount = getTypeOrNull(data?.currentAccount);

  if (!currentAccount?.blocked) {
    return null;
  }

  return (
    <div className={styles.blockedBanner}>
      <Banner colorSet="negative" contextual role="alert">
        <Text variant="bodyMedium">
          <strong>This account has been blocked.</strong> Please contact support
          for assistance.
        </Text>
      </Banner>
    </div>
  );
};

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const Root = ({ children }: PropsWithChildren) => {
  useRefreshBillingAfterCheckout();
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    error,
    identity,
    orgName,
  } = useAuth();
  const preferredUrl = useFeatureFlag(
    'preferred-app-for-org.preferred-url',
    '',
  );
  const appRedirect = useAppRedirect(preferredUrl);
  const region = useRegion();
  const autoTrackRef = useAutoTracking();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loginStartError, setLoginStartError] = useState<Error>();
  const [invitationReturnRoute, setInvitationReturnRoute] = useState<string>();
  const hasAttemptedOrgLoginRef = useRef(false);
  const invitationRoute = getOrganizationInvitationRoute(location);

  const shouldLogin = searchParams.get('login') === 'true';
  const organization = searchParams.get('organization') ?? undefined;
  const isWaitingForAccessToken = isAuthenticated && !region;

  useSentryScope({ isAuthenticated, identity, orgName, region });

  useEffect(() => {
    if (isAuthenticated) {
      clearInvitationLoginReturn();
      setInvitationReturnRoute(undefined);
      return;
    }

    if (location.pathname === '/invitation') {
      setInvitationReturnRoute(undefined);
      return;
    }

    if (error && hasOAuthCallbackParams(searchParams)) {
      setInvitationReturnRoute(consumeInvitationLoginReturnRoute());
    }
  }, [error, isAuthenticated, location.pathname, searchParams]);

  useEffect(() => {
    if (
      hasAttemptedOrgLoginRef.current ||
      isLoading ||
      isAuthenticated ||
      !shouldLogin ||
      !organization
    ) {
      return;
    }

    hasAttemptedOrgLoginRef.current = true;
    setLoginStartError(undefined);
    void loginWithRedirect({
      authorizationParams: {
        organization,
      },
      appState: {
        returnTo: getCurrentLocationPath(['login', 'organization']),
      },
    }).catch((loginError: unknown) => {
      setLoginStartError(toError(loginError));
    });
  }, [
    shouldLogin,
    organization,
    isLoading,
    isAuthenticated,
    loginWithRedirect,
  ]);

  if (invitationRoute) {
    return <Navigate to={invitationRoute} replace />;
  }

  if (isPublicRoute(location.pathname)) {
    return <Fragment>{children}</Fragment>;
  }

  if (isLoading || isWaitingForAccessToken) {
    return <LoadingPage />;
  }

  if (invitationReturnRoute) {
    return <Navigate to={invitationReturnRoute} replace />;
  }

  if (!isAuthenticated) {
    return (
      <LoginPage
        onLogin={() => {
          setLoginStartError(undefined);
          void loginWithRedirect({
            appState: { returnTo: getCurrentLocationPath() },
          }).catch((loginError: unknown) => {
            setLoginStartError(toError(loginError));
          });
        }}
        loading={isLoading}
        error={loginStartError ?? error}
      />
    );
  }

  if (appRedirect.shouldRedirect) {
    return <AppRedirectPage targetUrl={appRedirect.targetUrl} />;
  }

  return (
    <div ref={autoTrackRef}>
      <PageTrackingGuard />
      {resourceLinkExtensions}
      <BlockedAccountBanner />
      <AppAccountRedirect />
      <TourResumer />
      {children}
    </div>
  );
};

export const RootWrapper = AppRootWrapperBlueprint.make({
  name: 'AppRootWrapper',
  params: {
    component: ({ children }) => {
      return compatWrapper(
        <ConfidenceProvider confidence={confidenceApi}>
          <FlagEvaluationProvider fallback={<LoadingPage />}>
            <PageTrackingProvider>
              <ConfidenceCoreProvider>
                <FlagContext>
                  <PulseCaptureNotificationsProvider>
                    <RunningChatsProvider>
                      <Root>{children}</Root>
                    </RunningChatsProvider>
                  </PulseCaptureNotificationsProvider>
                </FlagContext>
              </ConfidenceCoreProvider>
            </PageTrackingProvider>
          </FlagEvaluationProvider>
        </ConfidenceProvider>,
      );
    },
  },
});
